// Everything the starred-chip control needs to turn form values into request
// criteria, kept free of React so it can be tested directly.
//
// The wire shape is documented in Be_Auth_DevOps/docs/keyword-search-api-request-contract.md.
// One parameter — `keywordGroups` — covers the common Keywords box, Skills, and
// any field an admin turns the capability on for later, so adding a new starred
// field is config rather than a new request parameter on both sides.

import { tokenizeBooleanQuery, validateBooleanQuery } from './booleanQueryUtils';

export const KEYWORD_FIELD_TYPE = 'keywords';
export const DEFAULT_MAX_KEYWORDS = 50;

function truthy(value) {
  return value === true || value === 1 || value === '1';
}

function toTerm(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' || typeof raw === 'number') {
    const value = String(raw).trim();
    return value ? { value, mandatory: false } : null;
  }
  if (typeof raw === 'object') {
    const value = String(raw.value ?? raw.label ?? raw.name ?? '').trim();
    return value ? { value, mandatory: Boolean(raw.mandatory) } : null;
  }
  return null;
}

// One value object holds BOTH modes — the chips and the expression — which is
// what makes flipping the Boolean toggle preserve everything instead of
// behaving like Reset. It also accepts the plain string[] a field stored while
// it was still a `tagify`, so converting Skills to starred chips needs no data
// migration.
const EMPTY_VALUE = { terms: [], mandatoryAll: false, expression: '', generatedExpression: '' };

export function normalizeKeywordValue(raw) {
  if (Array.isArray(raw)) {
    return { ...EMPTY_VALUE, terms: raw.map(toTerm).filter(Boolean) };
  }
  if (raw && typeof raw === 'object') {
    return {
      terms: Array.isArray(raw.terms) ? raw.terms.map(toTerm).filter(Boolean) : [],
      mandatoryAll: Boolean(raw.mandatoryAll),
      expression: typeof raw.expression === 'string' ? raw.expression : '',
      // What the mode toggle last wrote into the expression box. Comparing the
      // box against it is how "still the carried-over list" is told apart from
      // "the user has edited this" — without it, an edited query would be
      // overwritten the next time the toggle was flipped.
      generatedExpression: typeof raw.generatedExpression === 'string' ? raw.generatedExpression : '',
    };
  }
  if (typeof raw === 'string' && raw.trim()) {
    return { ...EMPTY_VALUE, terms: [{ value: raw.trim(), mandatory: false }] };
  }
  return { ...EMPTY_VALUE };
}

export function isKeywordField(field) {
  return field?.type === KEYWORD_FIELD_TYPE;
}

// The one field that owns the Boolean toggle and the expression box. Every
// other keyword field is just chips.
export function isPrimaryKeywordField(field) {
  return isKeywordField(field) && truthy(field.isPrimaryKeyword);
}

export function isExcludeKeywordField(field) {
  return String(field?.keywordRole ?? 'include').toLowerCase() === 'exclude';
}

// The per-field admin switch for stars. Off — the default — means chips with no
// stars and no "mark all" checkbox, which is why only the common Keywords box
// and Skills show them today. A mandatory exclusion has no meaning, so an
// exclude field never shows stars however it is configured.
export function showsMandatoryStars(field) {
  return truthy(field?.showMandatoryOption) && !isExcludeKeywordField(field);
}

export function keywordGroupId(field) {
  const configured = String(field?.keywordGroup ?? '').trim();
  return configured || String(field?.field ?? '');
}

// Target Mongo paths. Empty means "the whole searchFields list", i.e. search
// the entire resume — the same rule as a targeted group, not a separate mode.
export function keywordTargetFields(field) {
  const raw = field?.keywordFields;
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return list.map((entry) => String(entry ?? '').trim()).filter(Boolean);
}

export function keywordLimit(field) {
  const configured = Number(field?.maxKeywords);
  return Number.isFinite(configured) && configured > 0 ? Math.floor(configured) : DEFAULT_MAX_KEYWORDS;
}

function uniq(values) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
}

// Whether this field contributes anything, used for both the Search-button gate
// and the required-field check. The primary field in Boolean mode is judged on
// its expression; everything else on its chips.
export function hasKeywordValue(value, { booleanMode = false, isPrimary = false } = {}) {
  const model = normalizeKeywordValue(value);
  if (booleanMode && isPrimary) return model.expression.trim() !== '';
  return model.terms.length > 0;
}

// ---------------------------------------------------------------------------
// Carrying values between the two modes
//
// The two directions are deliberately NOT symmetrical:
//
//   chips → expression   join the raw values with commas, and nothing else. No
//                        AND is invented from a star, no OR from a set. Writing
//                        operators the user did not type would put words in
//                        their mouth.
//   expression → chips   read the operators that ARE there. AND → starred,
//                        OR → unstarred, NOT → the Exclude box. Anything with
//                        no chip representation is left alone rather than
//                        flattened into a different search.
//
// Either way both halves of the value survive, so nothing is ever lost.
// ---------------------------------------------------------------------------

// A plain comma list: each segment is one chip, spaces and all, so
// "java spring, javadeveloper" is two chips and "java spring" is one.
function splitKeywordList(text) {
  return uniq(
    String(text ?? '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean),
  );
}

const EMPTY_ANALYSIS = { mandatory: [], optional: [], exclude: [] };

function isOperator(token, word) {
  return token?.type === 'operator' && token.value === word;
}

// Reads a run of tokens as `t (OR t)*`, the only shape an optional set can take.
// Returns null for anything else — an AND inside the run, a stray paren, an
// empty run.
function readOrChain(tokens) {
  if (!tokens.length) return null;
  const out = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (i % 2 === 0) {
      if (token.type !== 'term' || !String(token.value).trim()) return null;
      out.push(String(token.value).trim());
    } else if (!isOperator(token, 'OR')) {
      return null;
    }
  }
  // A trailing operator leaves an even length.
  return tokens.length % 2 === 1 ? out : null;
}

// Splits a token run into top-level segments.
//
// `AND` ends a segment and is dropped. `NOT` also ends one but STARTS the next,
// because it is not preceded by an AND in this grammar — "a AND b NOT c"
// tokenizes as term AND term NOT term, so splitting on AND alone would never
// see the exclusion.
//
// Returns null when the parentheses nest more than one deep: a nested group has
// no chip representation, so there is nothing to be gained by parsing further.
// Empty segments are dropped rather than rejected — "a AND NOT b" legitimately
// produces one, and genuinely malformed input was already refused by
// validateBooleanQuery before this runs.
function splitIntoSegments(tokens) {
  const segments = [];
  let current = [];
  let depth = 0;

  const flush = () => {
    if (current.length) segments.push(current);
    current = [];
  };

  for (const token of tokens) {
    if (token.type === 'lparen') {
      depth += 1;
      if (depth > 1) return null;
    } else if (token.type === 'rparen') {
      depth -= 1;
      if (depth < 0) return null;
    }
    if (depth === 0 && isOperator(token, 'AND')) {
      flush();
      continue;
    }
    if (depth === 0 && isOperator(token, 'NOT')) {
      flush();
      current.push(token);
      continue;
    }
    current.push(token);
  }
  if (depth !== 0) return null;
  flush();
  return segments;
}

// Strips one wrapping pair of parentheses, or returns the run unchanged.
function unwrapGroup(tokens) {
  if (tokens.length >= 2 && tokens[0].type === 'lparen' && tokens[tokens.length - 1].type === 'rparen') {
    return { inner: tokens.slice(1, -1), wrapped: true };
  }
  return { inner: tokens, wrapped: false };
}

// Turns an expression back into the three chip buckets, or returns null when it
// simply cannot be one: chips carry a SINGLE optional set, so anything needing
// two of them (or nesting, or ambiguous precedence) has no faithful
// representation and must be left alone rather than flattened.
export function analyseKeywordExpression(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return null;

  // Anything the grammar itself rejects — dangling or doubled operators,
  // unbalanced parens, an unterminated quote, an empty group — is refused here
  // rather than re-deriving those rules. It is the same check the Search button
  // already runs, so the two can never disagree about what is valid.
  if (validateBooleanQuery(raw).errors.length) return null;

  const tokens = tokenizeBooleanQuery(raw);
  if (!tokens.length) return null;

  const hasOperator = tokens.some((token) => token.type === 'operator');
  const hasParen = tokens.some((token) => token.type === 'lparen' || token.type === 'rparen');

  // No operators and no grouping — it is a list, not a query. Commas are the
  // only separator; a segment keeps its spaces.
  if (!hasOperator && !hasParen) {
    const optional = splitKeywordList(raw);
    return optional.length ? { ...EMPTY_ANALYSIS, optional } : null;
  }

  const segments = splitIntoSegments(tokens);
  if (!segments || !segments.length) return null;

  const mandatory = [];
  const exclude = [];
  let optional = null; // at most one set

  for (const segment of segments) {
    if (isOperator(segment[0], 'NOT')) {
      const { inner } = unwrapGroup(segment.slice(1));
      const terms = readOrChain(inner);
      if (!terms) return null;
      exclude.push(...terms);
      continue;
    }

    const { inner, wrapped } = unwrapGroup(segment);
    if (wrapped) {
      const terms = readOrChain(inner);
      if (!terms) return null;
      // Two optional groups cannot both be chips.
      if (optional) return null;
      optional = terms;
      continue;
    }

    if (segment.length === 1 && segment[0].type === 'term') {
      mandatory.push(String(segment[0].value).trim());
      continue;
    }

    // A bare OR chain is only unambiguous when it is the whole expression;
    // mixed with a top-level AND, precedence decides the meaning and guessing
    // would change the search.
    if (segments.length === 1) {
      const terms = readOrChain(segment);
      if (!terms) return null;
      optional = terms;
      continue;
    }

    return null;
  }

  // One bare term reads the same starred or not, and unstarred is what typing a
  // single chip gives you.
  if (mandatory.length === 1 && !optional && !exclude.length) {
    return { mandatory: [], optional: uniq(mandatory), exclude: [] };
  }

  const result = {
    mandatory: uniq(mandatory),
    optional: uniq(optional ?? []),
    exclude: uniq(exclude),
  };
  const total = result.mandatory.length + result.optional.length + result.exclude.length;
  return total ? result : null;
}

// Traditional → Boolean. Fills the box with the chips as a comma list, but only
// when it is empty or still holds exactly what was put there last time — a query
// the user typed or edited is never overwritten.
export function carryTermsToExpression(raw) {
  const model = normalizeKeywordValue(raw);
  const current = model.expression.trim();
  if (current !== '' && current !== model.generatedExpression.trim()) return model;

  const joined = model.terms.map((term) => term.value).join(', ');
  return { ...model, expression: joined, generatedExpression: joined };
}

// Boolean → Traditional. Rebuilds the chips when the expression can be one, and
// returns the value untouched when it cannot.
//
// The two star rules point deliberately in opposite directions. From an
// expression the operators say it outright. From a plain list there is nothing
// to read, so a value that was already a chip KEEPS its star (losing a mandatory
// mark to a mode switch would quietly widen the search) and a value typed in
// Boolean mode arrives UNSTARRED (promoting it would quietly narrow it).
export function carryExpressionToTerms(raw, { starsEnabled = true } = {}) {
  const model = normalizeKeywordValue(raw);
  const analysis = analyseKeywordExpression(model.expression);
  if (!analysis) return { model, exclude: null, converted: false };

  const wasMandatory = new Map(
    model.terms.map((term) => [term.value.toLowerCase(), term.mandatory]),
  );
  const fromList = analysis.mandatory.length === 0 && analysis.exclude.length === 0
    && !/[()"]/.test(model.expression)
    && !tokenizeBooleanQuery(model.expression).some((token) => token.type === 'operator');

  const star = (value, mandatory) => {
    if (!starsEnabled) return false;
    if (fromList) return Boolean(wasMandatory.get(value.toLowerCase()));
    return mandatory;
  };

  const terms = [
    ...analysis.mandatory.map((value) => ({ value, mandatory: star(value, true) })),
    ...analysis.optional.map((value) => ({ value, mandatory: star(value, false) })),
  ];

  return {
    model: {
      ...model,
      terms,
      mandatoryAll: terms.length > 0 && terms.every((term) => term.mandatory),
      // Box and chips agree again, so a later flip regenerates from the chips
      // rather than restoring a stale list.
      generatedExpression: model.expression,
    },
    exclude: analysis.exclude,
    converted: true,
  };
}

// Walks the whole form rather than one field at a time, because two config
// fields can feed one group — an "Exclude Keywords" box attaches to the
// Keywords box by naming the same `keywordGroup`, and they must arrive merged.
//
// In Boolean mode the primary field is skipped: its expression goes out as
// `query` instead. Other keyword fields keep contributing normally, so Skills
// chips still filter while the common box is in expression mode.
export function buildKeywordGroups(fields = [], values = {}, { booleanMode = false } = {}) {
  const groups = new Map();

  fields.forEach((field) => {
    if (!isKeywordField(field)) return;
    if (booleanMode && isPrimaryKeywordField(field)) return;

    const model = normalizeKeywordValue(values[field.field]);
    if (!model.terms.length) return;

    const id = keywordGroupId(field);
    if (!id) return;

    if (!groups.has(id)) {
      groups.set(id, { id, fields: keywordTargetFields(field), mandatory: [], optional: [], exclude: [] });
    }
    const group = groups.get(id);
    // The include field usually declares the paths; an exclude companion that
    // declares none inherits them rather than widening the group to the whole
    // resume.
    if (!group.fields.length) group.fields = keywordTargetFields(field);

    if (isExcludeKeywordField(field)) {
      model.terms.forEach((term) => group.exclude.push(term.value));
      return;
    }

    // A term is only mandatory when the admin turned stars on for this field —
    // otherwise a stale `mandatory: true` in a stored value could silently
    // narrow a search whose UI shows no stars at all.
    const starred = showsMandatoryStars(field);
    model.terms.forEach((term) => {
      if (starred && term.mandatory) group.mandatory.push(term.value);
      else group.optional.push(term.value);
    });
  });

  const out = [];
  groups.forEach((group) => {
    const entry = {
      id: group.id,
      fields: group.fields,
      mandatory: uniq(group.mandatory),
      optional: uniq(group.optional),
      exclude: uniq(group.exclude),
    };
    if (!entry.mandatory.length && !entry.optional.length && !entry.exclude.length) return;
    out.push(entry);
  });

  return out.length ? out : null;
}
